import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGTMDispatch } from '@elgorditosalsero/react-gtm-hook';
import dayjs from '@/shared/helpers/dayjs';
import _ from 'lodash';

import { ROUTES } from '@/shared/constants/routes';
import { GtmEvent } from '@/shared/types/enums';
import { CustomersStepTypeEnum, CustomersStepTypeEnumPaths } from '@/shared/endpoints/customers/models/enums';
import { ICustomersUserAddressesResponse, ICustomersUserProfileRequest } from '@/shared/endpoints/customers';
import { UserGender } from '../../constants';

import { useAppDispatch, useAppSelector } from '@/shared/hooks/store';
import { hasPregnancy, selectOnBoardingInfo, selectUserProfileInfo, userAddressData } from '@/store/slices/onboarding/selectors';

import { CustomersController } from '@/shared/endpoints/customers';
import { OnboardingController } from '@/shared/endpoints/onboarding';

import AnimationFlow, { IAnimationSrc } from '../../components/animation-flow';
import AddressComponent from './address/address';
import AllergiesAndConditionsComponent, { AllergiesFormValues } from './components/allergies-and-conditions';
import AboutYouPregnancyComponent, { PregnancyDefaultValuesInterface } from './components/pregnancy';
import BasicInfoComponent, { AboutYouFormData } from './components/basic';

import { stepPayloadHandler, isNonNegativeNumber } from '@/modules/onboarding/helpers';
import { setPageType } from '@/shared/helpers/page-type';
import { usePostHog } from 'posthog-js/react'

import './index.css';
import { ExtendedAddress } from '@/shared/components/address-form/types';
interface AboutYouProps {
    introductionPassed: boolean;
    onIntroductionComplete(): void;
}

// DEPRECATED!!! This component is not used anymore

export const AboutYou: React.FC<AboutYouProps> = ({ introductionPassed, onIntroductionComplete = () => {} }) => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const posthog = usePostHog();
    const sendDataToGTM = useGTMDispatch();
    const { welcomeStepOverride } = location.state || {};
    const hasPregnancyPage = useAppSelector(hasPregnancy);
    const onboardingData = useAppSelector(selectOnBoardingInfo);
    const userProfileData = useAppSelector(selectUserProfileInfo);
    const userAddresses = useAppSelector(userAddressData);
    const navigate = useNavigate();
    const [animationData, setAnimationData] = useState<IAnimationSrc>();
    const [animationDataVertical, setAnimationDataVertical] = useState<IAnimationSrc>();

    useEffect(() => {
        // Dynamically import the JSON files
        const loadAnimations = async () => {
            const animation = await import('@/assets/animations/Bottles Animation with Text.json');
            const animationVertical = await import('@/assets/animations/01_Bottles Animation with Text - Vertical.json');
            setAnimationData(animation.default);
            setAnimationDataVertical(animationVertical.default);
        };

        loadAnimations();
    }, []);

    const [isLoading, setIsLoading] = useState(false);
    const [step, setStep] = useState<number>(isNonNegativeNumber(welcomeStepOverride) ? welcomeStepOverride : 0);

    const _conv_page_type = useMemo(() => {
        switch (step) {
            case 0:
                return 'about_basic_info';
            case 1:
                return 'about_address';
            case 2:
                return 'about_allergies_and_conditions';
            case 3:
                return 'about_pregnancy';
            default:
                return '';
        }
    }, [step]);

    useEffect(() => {
        setPageType(_conv_page_type);
    }, [_conv_page_type]);

    useEffect(() => {
        const state = location.state || {};
        if (isNonNegativeNumber(state.welcomeStepOverride)) {
            navigate(location.pathname, {
                state: { welcomeStepOverride: -1 }
            });
        }
    }, [location, navigate]);

    const getExistingAddress = useCallback((): ICustomersUserAddressesResponse => {
        if (userAddresses?.length) {
            const preferredAddress = userAddresses.find(address => address.preferred);
            return preferredAddress || userAddresses[0];
        }
        return {} as ICustomersUserAddressesResponse;
    }, [userAddresses]);

    const updateRemoteDetails = useCallback(
        async (data: { allergies?: number | null; conditions?: number | null; currentStep?: CustomersStepTypeEnum }) => {
            const payload = _.cloneDeep(onboardingData);
            if (payload && payload?.details) {
                // @ts-ignore-next-line
                payload.details = { ...payload.details, ...data };
                const update = await dispatch(CustomersController.updateMetadata(payload));
                return update;
            }
            return undefined;
        },
        [dispatch, onboardingData]
    );

    const createOrUpdateUserProfile = useCallback(
        async (values: Partial<ICustomersUserProfileRequest>) => {
            values.dob = values.dob ? dayjs(values.dob).format('YYYY-MM-DD') : '';
            if (!userProfileData || !userProfileData.id) {
                return await dispatch(
                    OnboardingController.createUserProfile({
                        ...values
                    })
                );
            }
            const userProfile = { ...userProfileData, ...values, ver: userProfileData.ver };
            return await dispatch(OnboardingController.updateUserProfile(userProfile));
        },
        [dispatch, userProfileData]
    );

    const _onBasicInfoSubmit = useCallback(
        async (values: AboutYouFormData) => {
            setIsLoading(true);
            let phone = '';
            values.communication = {
                Communication: []
            };
            // when no phone can be found in onboarding data, use the phone from user profile
            if (onboardingData?.details?.phoneNumber) {
                phone = onboardingData?.details?.phoneNumber;
            }
            // if it is already in UserProfile, use the value
            if (
                userProfileData?.communication &&
                userProfileData?.communication.Communication.length &&
                userProfileData?.communication.Communication[0].Number
            ) {
                phone = userProfileData?.communication.Communication[0].Number;
                values.communication = {
                    Communication: [...(userProfileData?.communication?.Communication || [])]
                };
            }
            values.communication.Communication[0] = {
                Number: phone,
                Qualifier: 'CP',
                Extension: ''
            };
            const birthDate = dayjs(values.dob);
            if (values.gender === UserGender.MALE) {
                values.pregnant = null;
            } else if (birthDate.add(55, 'years').isBefore(dayjs())) {
                values.pregnant = false;
            }
            const res = await createOrUpdateUserProfile(values);
            if (res.type.toLowerCase().indexOf('rejected') === -1) {
                // quick fix for backend 409 issue
                await dispatch(CustomersController.get());
                const preStoredData = localStorage.getItem('persistSlice:auth');
                // remove the data when data is saved
                if (preStoredData && JSON.parse(preStoredData).registerData) {
                    const registerData = JSON.parse(preStoredData).registerData;
                    localStorage.setItem(
                        'persistSlice:auth',
                        JSON.stringify({
                            ...JSON.parse(preStoredData),
                            registerData: {
                                ...registerData,
                                firstName: '',
                                lastName: ''
                            }
                        })
                    );
                }
                setStep(prev => prev + 1);
            } else {
                toast.error('Oops! Something went wrong. Please try again.');
            }
            setIsLoading(false);
        },
        [createOrUpdateUserProfile, onboardingData?.details?.phoneNumber, userProfileData?.communication, dispatch]
    );

    const _onAddressSubmit = useCallback(
        async (values: ExtendedAddress, passThrough?: boolean) => {
            if (!passThrough) {
                setIsLoading(true);
                // save all the data to the store, save them together in last submit
                const existingAddress = getExistingAddress();
                if (existingAddress && typeof existingAddress === 'object' && Object.keys(existingAddress).length > 0) {
                    await OnboardingController.deleteUserAddress(existingAddress);
                }
                if (!values.unit) values.unit = '';
                const res = await dispatch(OnboardingController.addUserAddress(values));
                setIsLoading(false);

                if (res.type.toLowerCase().indexOf('rejected') === -1) {
                    return setStep(prev => prev + 1);
                }
                toast.error('Oops! Something went wrong. Please try again.');
            } else {
                return setStep(prev => prev + 1);
            }
        },
        [dispatch, getExistingAddress]
    );

    const conditionalCompletionAboutYouGtmEvent = useCallback(
        (newStep: boolean, userId?: number) => {
            if (newStep) {
                sendDataToGTM({ event: GtmEvent.ABOUT_COMPLETED, userId });
                posthog?.capture(GtmEvent.ABOUT_COMPLETED);
            }
        },
        [posthog, sendDataToGTM]
    );

    const _onAllergiesAndConditionsSubmit = useCallback(
        async (values: AllergiesFormValues) => {
            setIsLoading(true);
            // dispatch to save the data to the backend
            if (!hasPregnancyPage && values.currentStep === CustomersStepTypeEnum.AboutYou) {
                values.currentStep = CustomersStepTypeEnum.Insurance;
            }
            const pronedAllergies = values.allergies
                ? values.allergies?.map(item => {
                      // eslint-disable-next-line @typescript-eslint/no-unused-vars
                      const { label, value, ...rest } = item;
                      return rest;
                  })
                : [];
            const pronedConditions = values.conditions
                ? values.conditions?.map(item => {
                      // eslint-disable-next-line @typescript-eslint/no-unused-vars
                      const { label, value, ...rest } = item;
                      return rest;
                  })
                : [];
            if (!userProfileData) return;
            const userProfile = {
                ...userProfileData,
                allergies: pronedAllergies,
                conditions: pronedConditions
            };
            const res = await dispatch(OnboardingController.updateUserProfile(userProfile));

            if (res.type.toLowerCase().indexOf('rejected') > -1) {
                setIsLoading(false);
                return toast.error('Oops! Something went wrong. Please try again.');
            }
            if (hasPregnancyPage) {
                const res = await updateRemoteDetails({
                    allergies: values.hasAllergies,
                    conditions: values.hasConditions
                });
                setIsLoading(false);
                if (res?.type.toLowerCase().indexOf('rejected') === -1) {
                    return setStep(step + 1);
                }
                toast.error('Oops! Something went wrong. Please try again.');
            } else {
                const stepPayload = stepPayloadHandler(onboardingData?.details?.currentStep, CustomersStepTypeEnum.Insurance);
                const updateDetails = {
                    allergies: values.hasAllergies,
                    conditions: values.hasConditions,
                    ...stepPayloadHandler(onboardingData?.details?.currentStep, CustomersStepTypeEnum.Insurance)
                };
                const res = await updateRemoteDetails(updateDetails);
                setIsLoading(false);
                conditionalCompletionAboutYouGtmEvent(!!stepPayload, userProfileData?.accountId);
                if (res?.type.toLowerCase().includes('rejected')) {
                    return toast.error('Oops! Something went wrong. Please try again.');
                }
                navigate(ROUTES.ONBOARDING.MAIN.replace(':stepType?', CustomersStepTypeEnumPaths.INSURANCE));
            }
        },
        [
            conditionalCompletionAboutYouGtmEvent,
            dispatch,
            hasPregnancyPage,
            navigate,
            step,
            onboardingData?.details?.currentStep,
            updateRemoteDetails,
            userProfileData
        ]
    );

    const _onPragnancySubmit = useCallback(
        async (values: PregnancyDefaultValuesInterface) => {
            if (!userProfileData) return;
            const updateUserProfileRes = await dispatch(
                OnboardingController.updateUserProfile({
                    ...userProfileData,
                    dob: values.dob,
                    pregnant: typeof values.pregnant === 'boolean' ? values.pregnant : undefined
                })
            );
            if (updateUserProfileRes.type.toLowerCase().indexOf('rejected') > -1) {
                setIsLoading(false);
                return toast.error('Oops! Something went wrong. Please try again.');
            }
            const stepPayload = stepPayloadHandler(onboardingData?.details?.currentStep, CustomersStepTypeEnum.Insurance);
            if (stepPayload) {
                await updateRemoteDetails({
                    ...stepPayload
                });
            }
            conditionalCompletionAboutYouGtmEvent(!!stepPayload, userProfileData?.accountId);
            navigate(ROUTES.ONBOARDING.MAIN.replace(':stepType?', CustomersStepTypeEnumPaths.INSURANCE));
        },
        [
            dispatch,
            conditionalCompletionAboutYouGtmEvent,
            navigate,
            onboardingData?.details?.currentStep,
            userProfileData,
            updateRemoteDetails
        ]
    );

    if (!userProfileData || !onboardingData) {
        return <></>;
    }

    if (!introductionPassed) {
        if (animationDataVertical && animationData) {
            return (
                <AnimationFlow
                    onSkip={() => {
                        setStep(0);
                        onIntroductionComplete();
                    }}
                    mobileSrc={animationDataVertical}
                    desktopSrc={animationData}
                />
            );
        }
        return null;
    }

    if (step === 0) {
        return (
            <BasicInfoComponent
                formArttributes={{
                    onFormSubmit: _onBasicInfoSubmit,
                    defaultValues: {
                        ...userProfileData
                    },
                    loading: isLoading
                }}
            />
        );
    }

    if (step === 1) {
        return (
            <AddressComponent
                formArttributes={{
                    onFormSubmit: _onAddressSubmit,
                    onGoBack: () => setStep(step - 1),
                    defaultValues: getExistingAddress(),
                    loading: isLoading,
                    autoDefault: true
                }}
            />
        );
    }

    if (step === 2) {
        return (
            <AllergiesAndConditionsComponent
                introductionPassed={introductionPassed}
                formArttributes={{
                    onFormSubmit: _onAllergiesAndConditionsSubmit,
                    onGoBack: () => setStep(step - 1),
                    defaultValues: {
                        ...userProfileData,
                        details: onboardingData.details
                    },
                    loading: isLoading
                }}
            />
        );
    }

    if (step === 3) {
        return (
            <AboutYouPregnancyComponent
                formArttributes={{
                    onFormSubmit: _onPragnancySubmit,
                    onGoBack: () => setStep(step - 1),
                    defaultValues: {
                        pregnant: userProfileData && userProfileData.pregnant !== undefined ? userProfileData.pregnant : null,
                        dob: userProfileData?.dob || ''
                    }
                }}
            />
        );
    }

    return <></>;
};

export default AboutYou;
